import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { createZendeskTicket } from '../services/api'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`

const ModalContent = styled.div`
  background: ${theme.color.background.light};
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: 1px solid ${theme.color.border.light};
  background: ${theme.color.background.frame};
  border-radius: 12px 12px 0 0;
`

const ModalTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2.4rem;
  color: ${theme.color.text.grey};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${theme.color.text.base};
  }
`

const Form = styled.form`
  padding: 2rem;
`

const FieldContainer = styled.div`
  margin-bottom: 2rem;
`

const Label = styled.label`
  display: block;
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.8rem;
`

const OptionalText = styled.span`
  font-weight: 400;
  color: ${theme.color.text.grey};
  font-style: italic;
`

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  font-size: 1.4rem;
  border: 1px solid ${props => props.hasError ? '#ff0000' : theme.color.border.light};
  border-radius: 4px;
  background: white;
  color: ${theme.color.text.base};

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff0000' : theme.color.accent.primary};
  }

  &::placeholder {
    color: ${theme.color.text.grey};
  }

  &:disabled {
    background: ${theme.color.background.frame};
    color: ${theme.color.text.grey};
    cursor: not-allowed;
  }
`

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  font-size: 1.4rem;
  border: 1px solid ${props => props.hasError ? '#ff0000' : theme.color.border.light};
  border-radius: 4px;
  background: white;
  color: ${theme.color.text.base};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff0000' : theme.color.accent.primary};
  }

  option {
    color: ${theme.color.text.base};
  }

  &:disabled {
    background: ${theme.color.background.frame};
    cursor: not-allowed;
  }
`

const TextArea = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  font-size: 1.4rem;
  border: 1px solid ${props => props.hasError ? '#ff0000' : theme.color.border.light};
  border-radius: 4px;
  background: white;
  color: ${theme.color.text.base};
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff0000' : theme.color.accent.primary};
  }

  &::placeholder {
    color: ${theme.color.text.grey};
  }
`

const ErrorText = styled.span`
  display: block;
  color: #ff0000;
  font-size: 1.2rem;
  margin-top: 0.5rem;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' ? `
    background: ${theme.color.accent.primary};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background: #b8002d;
    }

    &:disabled {
      background: ${theme.color.text.grey};
      cursor: not-allowed;
      opacity: 0.6;
    }
  ` : `
    background: transparent;
    color: ${theme.color.text.base};
    border: 1px solid ${theme.color.border.light};

    &:hover {
      background: ${theme.color.background.frame};
    }
  `}
`

const SuccessMessage = styled.div`
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  font-size: 1.4rem;
`

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  font-size: 1.4rem;
`

interface ContactSupportModalProps {
  isOpen: boolean
  onClose: () => void
  ticketType: 'customer' | 'employee'
  userData: any
  currentUser?: any
}

// Data from the contact form
const TOPIC_OPTIONS = [
  'Memberships, Packages, Gift cards',
  'Active, Upcoming, or Past reservations',
  "Barry's X",
  'Account Management',
  'The Academy',
  'Campaigns and Challenges',
  "Barry's Shop- Online Retail",
  'The Workout- How does it work?',
  'In Studio Experience',
  'Fuel Bar',
  'Community Marketing and Press',
  'Private Events and Group Bookings',
  'Partnerships',
  'New Studio Inquiries',
  'Career Opportunities',
  'General Feedback'
]

// Zendesk field IDs for subtopics by topic
const SUBTOPIC_FIELD_IDS: { [key: string]: number } = {
  'Memberships, Packages, Gift cards': 38164729625499,
  'Active, Upcoming, or Past reservations': 1900008409624,
  "Barry's X": 1260828734769,
  'Account Management': 1900008731484,
  'The Academy': 40895152948123,
  'Campaigns and Challenges': 40898745187995,
  "Barry's Shop- Online Retail": 5758395498395,
  'The Workout- How does it work?': 40917880479643,
  'In Studio Experience': 1260829050809,
  'Community Marketing and Press': 4418238300059,
  'New Studio Inquiries': 1260828895969,
  'Career Opportunities': 1260829009330
}

// Zendesk tag mappings - dropdown fields require tag values, not display labels
const REGION_TAGS: { [key: string]: string } = {
  'Arizona': 'arizona',
  'Northern California': 'northern_california',
  'Southern California': 'southern_california',
  'Colorado': 'colorado',
  'D.C. Metro': 'washington_dc_metro_area',
  'Florida': 'florida',
  'Georgia': 'georgia',
  'Illinois': 'illinois',
  'Massachusetts': 'massachusetts',
  'Minnesota': 'minnesota',
  'New Jersey': 'new_jersey',
  'New York': 'new_york',
  'North Carolina': 'north_carolina',
  'Oregon': 'oregon',
  'Pennsylvania': 'pennsylvania',
  'South Carolina': 'south_carolina',
  'Tennessee': 'tennessee',
  'Texas': 'texas',
  'Utah': 'utah',
  'Washington State': 'washington_state',
  'Barrys X': 'barry_s_x',
  'Canada': 'canada',
  'Germany': 'germany',
  'Italy': 'italy',
  'Spain': 'spain',
  'Sweden': 'sweden',
  'Switzerland': 'switzerland',
  'UAE': 'uae',
  'United Kingdom': 'uk'
}

const TOPIC_TAGS: { [key: string]: string } = {
  'Memberships, Packages, Gift cards': 'memberships_packages_and_gift_cards',
  'Active, Upcoming, or Past reservations': 'active_upcoming_or_past_reservations',
  "Barry's X": "barry's_x",
  'Account Management': 'account_management',
  'The Academy': 'the_academy_loyalty_program',
  'Campaigns and Challenges': 'campaigns_and_challenges',
  "Barry's Shop- Online Retail": 'barry_s_shop_online_retail',
  'The Workout- How does it work?': 'the_workout_how_does_it_work',
  'In Studio Experience': 'studio_experience',
  'Fuel Bar': 'fuelbar',
  'Community Marketing and Press': 'community_marketing_and_press',
  'Private Events and Group Bookings': 'private_events_and_group_bookings',
  'Partnerships': 'partnerships',
  'New Studio Inquiries': 'new_studio_inquiries',
  'Career Opportunities': 'career_opportunities',
  'General Feedback': 'general_feedback'
}

// Per-region location field IDs from Zendesk (ticket-fields.csv)
const LOCATION_FIELD_IDS: { [key: string]: number } = {
  'Arizona': 13347713529883,
  'Northern California': 1900009081724,
  'Southern California': 1260829420629,
  'Colorado': 1260829336789,
  'D.C. Metro': 1900009083324,
  'Florida': 7618689202971,
  'Georgia': 1900009002584,
  'Illinois': 1260829401529,
  'Massachusetts': 1260829419629,
  'Minnesota': 1260829555350,
  'New Jersey': 33801198423963,
  'New York': 1900009081204,
  'North Carolina': 1260829420449,
  'Oregon': 1260829556190,
  'Pennsylvania': 1900009081744,
  'South Carolina': 34115050460955,
  'Tennessee': 1900009081804,
  'Texas': 1260829420849,
  'Utah': 35357957544091,
  'Washington State': 1260829421809,
  'Barrys X': 1260829421969,
  'Canada': 4415439920923,
  'Germany': 40973939640731,
  'Italy': 40974023189275,
  'Spain': 40974055934235,
  'Sweden': 40974120534683,
  'Switzerland': 40974225252763,
  'UAE': 40974206319259
}

// Location tags matched to per-region location CSV files (source of truth)
const LOCATION_TAGS: { [key: string]: { [key: string]: string } } = {
  'Arizona': { 'Scottsdale': 'scottsdale' },
  'Northern California': { 'Burlingame': 'burlingame', 'Castro': 'castro', 'FIDI': 'fidi', 'Marina': 'marina', 'Palo Alto': 'palo_alto', 'Santana Row': 'santana_row', 'Walnut Creek': 'walnut_creek' },
  'Southern California': { 'Brentwood': 'brentwood', 'Long Beach': 'long_beach', 'Newport Beach': 'newport_beach', 'Pasadena': 'pasadena', 'San Diego': 'san_diego', 'Santa Monica': 'santa_monica', 'South Bay': 'south_bay', 'Studio City': 'studio_city', 'Venice': 'venice', 'West Hollywood': 'west_hollywood' },
  'Colorado': { 'Denver': 'denver' },
  'D.C. Metro': { 'Arlington': 'arlington', 'DuPont Circle': 'dupont_circle', 'Navy Yard': 'navy_yard' },
  'Florida': { 'Aventura': 'aventura', 'Boca Raton': 'boca_raton', 'Miami Beach': 'miami_beach', 'Midtown': 'midtown', 'Tampa': 'tampa' },
  'Georgia': { 'Atlanta': 'atlanta', 'Midtown Atlanta': 'midtown_atlanta' },
  'Illinois': { 'Lincoln Park': 'lincoln_park', 'River North': 'river_north', 'Wicker Park': 'wicker_park' },
  'Massachusetts': { 'Back Bay': 'back_bay', 'Chestnut Hill': 'chestnut_hill', 'Downtown Crossing': 'downtown_crossing' },
  'Minnesota': { 'Minneapolis': 'minneapolis' },
  'New Jersey': { 'Hoboken': 'hoboken' },
  'New York': { 'Brooklyn Heights': 'brooklyn_heights', 'Chelsea': 'chelsea', 'East 64th': 'east_64th', 'East 86th': 'east_86th', 'Long Island City': 'long_island_city', 'Noho': 'noho', 'Park Ave South': 'park_ave_south', 'Roslyn': 'roslyn', 'Scarsdale': 'scarsdale', 'Southampton': 'southhampton', 'Tribeca': 'tribeca', 'Upper West Side': 'upper_west_side', 'Wainscott': 'wainscott', 'Wall Street': 'wall_street', 'Williamsburg': 'williamsburg' },
  'North Carolina': { 'Charlotte': 'charlotte' },
  'Oregon': { 'Portland': 'portland' },
  'Pennsylvania': { 'Philadelphia': 'philadelphia' },
  'South Carolina': { 'Charleston': 'charleston' },
  'Tennessee': { 'Nashville': 'nashville' },
  'Texas': { 'Austin': 'austin', 'Dallas': 'dallas', 'Houston': 'houston' },
  'Utah': { 'Salt Lake': 'salt_lake' },
  'Washington State': { 'Seattle': 'seattle' },
  'Canada': { 'Calgary': 'calgary', 'Richmond': 'richmond', 'Vancouver': 'vancouver', 'Yorkville': 'yorkville' },
  'Germany': { 'Berlin': 'berlin', 'Frankfurt': 'frankfurt' },
  'Italy': { 'Senato': 'senato' },
  'Spain': { 'Madrid': 'madrid' },
  'Sweden': { 'Gothenburg': 'gothenburg', 'Stockholm': 'stockholm' },
  'Switzerland': { 'Zurich': 'zurich' },
  'UAE': { 'DIFC': 'difc', 'Dubai Marina': 'dubai_marina' }
}

const SUBTOPIC_TAGS: { [key: string]: { [key: string]: string } } = {
  'Memberships, Packages, Gift cards': {
    'Gift Cards': 'gift_card_cards',
    'First Timer Offers': 'first_timer_offers',
    'General Pricing': 'general_pricing',
    'Student, Corporate, or Special Pricing': 'student__corporate__or_special_pricing',
    'Class Package Adjustments': 'class_package_adjustments',
    'Membership Adjustments': 'membership_adjustments',
    'Membership Cancellation': 'membership_cancellation',
    'Troubleshooting': 'troubleshooting'
  },
  'Active, Upcoming, or Past reservations': {
    'Making a new reservation': 'making_a_new_reservation',
    'Canceling an Existing Reservation': 'canceling_an_existing_reservation',
    'Changing an Existing Reservation': 'changing_an_existing_reservation',
    'Past Reservation Assistance': 'past_reservation_assistance',
    'Third Party Reservation Assistance - ClassPass, GymPass, WellHub': 'third_party_reservation_assistance'
  },
  "Barry's X": {
    'App assistance and Technical support': 'barrys_x_app_assistance_and_technical_support',
    'Membership Assistance': 'barrys_x_membership_assistance',
    'General Questions': 'barrys_x_general_questions'
  },
  'Account Management': {
    'Merging Multiple Accounts': 'merging_multiple_accounts',
    'Communication/Privacy Settings': 'communication/privacy_settings',
    'Updating Payment method': 'updating_payment_method',
    'Login or Technical Support': 'login_or_technical_support',
    'Deleting my account': 'deleting_my_account'
  },
  'The Academy': {
    'Login or Sign Up Assistance': 'login_or_sign_up_assistance',
    'Star Generation': 'star_generation',
    'Perks or Rewards': 'perks_or_rewards',
    'Quarterly Transition or Tier Assistance': 'quarterly_transition_or_tier_assistance',
    'General Questions': 'general_questions_the_academy'
  },
  'Campaigns and Challenges': {
    'Sign Up Inquiries': 'sign_up_inquiries',
    'Finisher Inquiries': 'finisher_inquiries',
    'Rewards and Prize Redemptions': 'rewards_and_prize_redemptions'
  },
  "Barry's Shop- Online Retail": {
    'Order Status': 'order_status',
    'Product Sizing': 'product_sizing',
    'Return/Exchange': 'return/exchange',
    'Lost/Delayed Items': 'lost/delayed_items',
    'Refund Status': 'refund_status',
    'Troubleshooting or General Questions': 'troubleshooting_or_general_questions'
  },
  'The Workout- How does it work?': {
    'RUN x LIFT': 'run_x_lift',
    'RIDE x LIFT': 'ride_x_lift',
    'LIFT': 'lift',
    'Days of the Week and Schedule': 'days_of_the_week_and_schedule',
    'Booking Inquiries': 'booking_inquiries',
    'Injuries or other Modification Needs': 'injuries_or_other_modification_needs'
  },
  'In Studio Experience': {
    'Instructor Feedback': 'instructor_feedback',
    'Class Schedule Feedback': 'class_schedule_feedback',
    'Locker Room / Facilities Feedback': 'facilities_feedback',
    'Lobby / Studio Staff Feedback': 'lobby_studio_staff_feedback'
  },
  'Community Marketing and Press': {
    'Donation Requests': 'community_marketing_and_press__donations'
  },
  'New Studio Inquiries': {
    'Domestic': 'new_studio_domestic',
    'International': 'new_studio_international'
  },
  'Career Opportunities': {
    'Career Page on our website': 'career_page_on_our_website'
  }
}

const SUBTOPICS_BY_TOPIC: { [key: string]: string[] } = {
  'Memberships, Packages, Gift cards': [
    'Gift Cards',
    'First Timer Offers',
    'General Pricing',
    'Student, Corporate, or Special Pricing',
    'Class Package Adjustments',
    'Membership Adjustments',
    'Membership Cancellation',
    'Troubleshooting'
  ],
  'Active, Upcoming, or Past reservations': [
    'Making a new reservation',
    'Canceling an Existing Reservation',
    'Changing an Existing Reservation',
    'Past Reservation Assistance',
    'Third Party Reservation Assistance - ClassPass, GymPass, WellHub'
  ],
  "Barry's X": [
    'App assistance and Technical support',
    'Membership Assistance',
    'General Questions'
  ],
  'Account Management': [
    'Merging Multiple Accounts',
    'Communication/Privacy Settings',
    'Updating Payment method',
    'Login or Technical Support',
    'Deleting my account'
  ],
  'The Academy': [
    'Login or Sign Up Assistance',
    'Star Generation',
    'Perks or Rewards',
    'Quarterly Transition or Tier Assistance',
    'General Questions'
  ],
  'Campaigns and Challenges': [
    'Sign Up Inquiries',
    'Finisher Inquiries',
    'Rewards and Prize Redemptions'
  ],
  "Barry's Shop- Online Retail": [
    'Order Status',
    'Product Sizing',
    'Return/Exchange',
    'Lost/Delayed Items',
    'Refund Status',
    'Troubleshooting or General Questions'
  ],
  'The Workout- How does it work?': [
    'RUN x LIFT',
    'RIDE x LIFT',
    'LIFT',
    'Days of the Week and Schedule',
    'Booking Inquiries',
    'Injuries or other Modification Needs'
  ],
  'In Studio Experience': [
    'Instructor Feedback',
    'Class Schedule Feedback',
    'Locker Room / Facilities Feedback',
    'Lobby / Studio Staff Feedback'
  ],
  'Community Marketing and Press': [
    'Donation Requests'
  ],
  'New Studio Inquiries': [
    'Domestic',
    'International'
  ]
}

const REGIONS = [
  'Arizona', 'Northern California', 'Southern California', 'Colorado', 'D.C. Metro', 'Florida',
  'Georgia', 'Illinois', 'Massachusetts', 'Minnesota', 'New Jersey', 'New York', 'North Carolina',
  'Oregon', 'Pennsylvania', 'South Carolina', 'Tennessee', 'Texas', 'Utah', 'Washington State',
  'Barrys X', 'Canada', 'Germany', 'Italy', 'Spain', 'Sweden', 'Switzerland', 'UAE', 'United Kingdom'
]

const STUDIOS_BY_REGION: { [key: string]: string[] } = {
  'Arizona': ['Scottsdale'],
  'Northern California': ['Burlingame', 'Castro', 'FIDI', 'Marina', 'Palo Alto', 'Santana Row', 'Walnut Creek'],
  'Southern California': ['Brentwood', 'Long Beach', 'Newport Beach', 'Pasadena', 'San Diego', 'Santa Monica', 'South Bay', 'Studio City', 'Venice', 'West Hollywood'],
  'Colorado': ['Denver'],
  'D.C. Metro': ['Arlington', 'DuPont Circle', 'Navy Yard'],
  'Florida': ['Aventura', 'Boca Raton', 'Miami Beach', 'Midtown', 'Tampa'],
  'Georgia': ['Atlanta', 'Midtown Atlanta'],
  'Illinois': ['Lincoln Park', 'River North', 'Wicker Park'],
  'Massachusetts': ['Back Bay', 'Chestnut Hill', 'Downtown Crossing'],
  'Minnesota': ['Minneapolis'],
  'New Jersey': ['Hoboken'],
  'New York': ['Brooklyn Heights', 'Chelsea', 'East 64th', 'East 86th', 'Long Island City', 'Noho', 'Park Ave South', 'Roslyn', 'Scarsdale', 'Southampton', 'Tribeca', 'Upper West Side', 'Wainscott', 'Wall Street', 'Williamsburg'],
  'North Carolina': ['Charlotte'],
  'Oregon': ['Portland'],
  'Pennsylvania': ['Philadelphia'],
  'South Carolina': ['Charleston'],
  'Tennessee': ['Nashville'],
  'Texas': ['Austin', 'Dallas', 'Houston'],
  'Utah': ['Salt Lake'],
  'Washington State': ['Seattle'],
  'Barrys X': ['Virtual Classes'],
  'Canada': ['Calgary', 'Richmond', 'Vancouver', 'Yorkville'],
  'Germany': ['Berlin', 'Frankfurt'],
  'Italy': ['Senato'],
  'Spain': ['Madrid'],
  'Sweden': ['Gothenburg', 'Stockholm'],
  'Switzerland': ['Zurich'],
  'UAE': ['DIFC', 'Dubai Marina'],
  'United Kingdom': ['Canary Wharf', 'Liverpool', 'London Central', 'London East', 'London SW1', 'London West', 'Manchester', 'Soho', 'St. Paul\'s']
}

const ContactSupportModal: React.FC<ContactSupportModalProps> = ({
  isOpen,
  onClose,
  ticketType,
  userData,
  currentUser
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    region: '',
    location: '',
    topic: '',
    subtopic: '',
    subject: '',
    comment: ''
  })

  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Pre-fill user data when modal opens or userData/currentUser changes
  useEffect(() => {
    if (isOpen) {
      if (ticketType === 'employee' && currentUser) {
        // For employee tickets, use logged-in employee info
        setFormData(prev => ({
          ...prev,
          name: currentUser.name || currentUser.preferred_username || '',
          email: currentUser.email || ''
        }))
      } else if (ticketType === 'customer' && userData) {
        // For customer tickets, use searched customer info
        setFormData(prev => ({
          ...prev,
          name: userData?.user?.first_name && userData?.user?.last_name
            ? `${userData.user.first_name} ${userData.user.last_name}`
            : '',
          email: userData?.user?.email || ''
        }))
      }
    }
  }, [isOpen, userData, currentUser, ticketType])

  if (!isOpen) return null

  const getTicketTypeLabel = () => {
    switch (ticketType) {
      case 'customer':
        return 'Customer Ticket'
      case 'employee':
        return 'Employee Ticket'
      default:
        return 'Support Request'
    }
  }

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value }

      // Reset location when region changes
      if (field === 'region') {
        newData.location = ''
      }

      // Reset subtopic when topic changes
      if (field === 'topic') {
        newData.subtopic = ''
      }

      return newData
    })

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    // Name and email are pre-filled and disabled, no validation needed
    if (!formData.region) {
      newErrors.region = 'Region is required'
    }
    if (!formData.location) {
      newErrors.location = 'Location is required'
    }
    if (!formData.topic) {
      newErrors.topic = 'Topic/Issue is required'
    }
    if (SUBTOPICS_BY_TOPIC[formData.topic] && !formData.subtopic) {
      newErrors.subtopic = 'Subtopic is required'
    }
    if (!formData.comment.trim()) {
      newErrors.comment = 'Comment is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError('')
    setSubmitSuccess(false)

    try {
      const subject = formData.subject || `${formData.topic}${formData.subtopic ? ` - ${formData.subtopic}` : ''}`

      const tags = [ticketType]

      // Get logged-in user's email
      const submitterEmail = currentUser?.email || ''

      // Build comment with customer info at the top for employee tickets
      let commentWithDetails = formData.comment
      if (ticketType === 'employee' && userData?.user) {
        const customerEmail = userData.user.email || 'N/A'
        const customerName = `${userData.user.first_name || ''} ${userData.user.last_name || ''}`.trim() || 'N/A'
        commentWithDetails = [
          `customer email address: ${customerEmail}`,
          `customer name: ${customerName}`,
          '',
          formData.comment
        ].join('\n')
      }

      // Convert display values to Zendesk tag values
      const regionTag = REGION_TAGS[formData.region] || formData.region.toLowerCase().replace(/\s+/g, '_')
      const topicTag = TOPIC_TAGS[formData.topic] || formData.topic.toLowerCase().replace(/\s+/g, '_')
      const locationTag = LOCATION_TAGS[formData.region]?.[formData.location] || formData.location.toLowerCase().replace(/\s+/g, '_')

      // Build custom fields array with tag values
      const locationFieldId = LOCATION_FIELD_IDS[formData.region]
      const customFields = [
        { id: 1900007222284, value: regionTag },
        { id: 1260827787830, value: topicTag },
        { id: 1260825900050, value: '1260813793450' },
        { id: 1260825900030, value: '1260813793450' }
      ]
      // Send location to the region-specific field (not the old Studio Location field)
      if (locationFieldId && locationTag) {
        customFields.push({ id: locationFieldId, value: locationTag })
      }

      // Add subtopic field if available (using tag value)
      if (formData.subtopic && formData.topic && SUBTOPIC_FIELD_IDS[formData.topic]) {
        const subtopicTag = SUBTOPIC_TAGS[formData.topic]?.[formData.subtopic] || formData.subtopic.toLowerCase().replace(/\s+/g, '_')
        customFields.push({ id: SUBTOPIC_FIELD_IDS[formData.topic], value: subtopicTag })
      }

      await createZendeskTicket({
        subject,
        comment: commentWithDetails,
        requester_email: formData.email,
        requester_name: formData.name,
        tags,
        group_id: 1260813793450,
        follower_email: submitterEmail,
        custom_fields: customFields
      })

      setSubmitSuccess(true)

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose()
        setSubmitSuccess(false)
      }, 2000)

    } catch (error) {
      console.error('Failed to submit support request:', error)
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit support request')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
      // Reset form state after closing
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          region: '',
          location: '',
          topic: '',
          subtopic: '',
          subject: '',
          comment: ''
        })
        setErrors({})
        setSubmitSuccess(false)
        setSubmitError('')
      }, 300)
    }
  }

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{getTicketTypeLabel()}</ModalTitle>
          <CloseButton onClick={handleClose} disabled={isSubmitting}>×</CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          {submitSuccess && (
            <SuccessMessage>
              Support request submitted successfully! Ticket has been created.
            </SuccessMessage>
          )}

          {submitError && (
            <ErrorMessage>{submitError}</ErrorMessage>
          )}

          <FieldContainer>
            <Label>{ticketType === 'employee' ? 'Employee Name' : 'Customer Name'}</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => updateFormData('name', e.target.value)}
              placeholder=""
              disabled={true}
              readOnly
            />
          </FieldContainer>

          <FieldContainer>
            <Label>{ticketType === 'employee' ? 'Employee Email' : 'Customer Email'}</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => updateFormData('email', e.target.value)}
              placeholder=""
              disabled={true}
              readOnly
            />
          </FieldContainer>

          <FieldContainer>
            <Label>Region *</Label>
            <Select
              value={formData.region}
              onChange={(e) => updateFormData('region', e.target.value)}
              hasError={!!errors.region}
              disabled={isSubmitting}
            >
              <option value="">Select a region</option>
              {REGIONS.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </Select>
            {errors.region && <ErrorText>{errors.region}</ErrorText>}
          </FieldContainer>

          {formData.region && STUDIOS_BY_REGION[formData.region] && (
            <FieldContainer>
              <Label>Location *</Label>
              <Select
                value={formData.location}
                onChange={(e) => updateFormData('location', e.target.value)}
                hasError={!!errors.location}
                disabled={isSubmitting}
              >
                <option value="">Select a location</option>
                {STUDIOS_BY_REGION[formData.region].map(studio => (
                  <option key={studio} value={studio}>{studio}</option>
                ))}
              </Select>
              {errors.location && <ErrorText>{errors.location}</ErrorText>}
            </FieldContainer>
          )}

          <FieldContainer>
            <Label>Topic/Issue *</Label>
            <Select
              value={formData.topic}
              onChange={(e) => updateFormData('topic', e.target.value)}
              hasError={!!errors.topic}
              disabled={isSubmitting}
            >
              <option value="">Select a topic</option>
              {TOPIC_OPTIONS.map(topic => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </Select>
            {errors.topic && <ErrorText>{errors.topic}</ErrorText>}
          </FieldContainer>

          {formData.topic && SUBTOPICS_BY_TOPIC[formData.topic] && (
            <FieldContainer>
              <Label>Subtopic *</Label>
              <Select
                value={formData.subtopic}
                onChange={(e) => updateFormData('subtopic', e.target.value)}
                hasError={!!errors.subtopic}
                disabled={isSubmitting}
              >
                <option value="">Select a subtopic</option>
                {SUBTOPICS_BY_TOPIC[formData.topic].map(subtopic => (
                  <option key={subtopic} value={subtopic}>{subtopic}</option>
                ))}
              </Select>
              {errors.subtopic && <ErrorText>{errors.subtopic}</ErrorText>}
            </FieldContainer>
          )}

          <FieldContainer>
            <Label>Subject <OptionalText>(optional)</OptionalText></Label>
            <Input
              type="text"
              value={formData.subject}
              onChange={(e) => updateFormData('subject', e.target.value)}
              placeholder="Brief subject line"
              disabled={isSubmitting}
            />
          </FieldContainer>

          <FieldContainer>
            <Label>Comment *</Label>
            <TextArea
              value={formData.comment}
              onChange={(e) => updateFormData('comment', e.target.value)}
              placeholder="Please describe your question or issue in detail"
              hasError={!!errors.comment}
              disabled={isSubmitting}
            />
            {errors.comment && <ErrorText>{errors.comment}</ErrorText>}
          </FieldContainer>

          <ButtonContainer>
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Send'}
            </Button>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </ModalOverlay>
  )
}

export default ContactSupportModal
